import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, AlertTriangle, X, Sparkles, Trash2, Loader2, CreditCard, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const ACCENT = "#d04260";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const formatTime = (seconds) => {
  if (seconds <= 0) return "Scaduto";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}g ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

/* ═══ Inline Payment Form (Stripe Elements) ═══ */
const PaymentForm = ({ plan, onSuccess, onCancel, token }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const pkg = plan === "yearly" ? { name: "Annuale", price: "€59,90/anno" } : { name: "Mensile", price: "€9,90/mese" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    const subId = localStorage.getItem("hostland_pending_sub");
    const returnUrl = `${window.location.origin}/payment/success?sub_id=${subId || ""}`;

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    // If no redirect happened, confirm subscription directly (with retry)
    if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
      if (subId) {
        let confirmed = false;
        for (let i = 0; i < 5; i++) {
          try {
            const res = await axios.post(
              `${API}/payments/confirm-subscription`,
              { subscription_id: subId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.status === "active") {
              confirmed = true;
              localStorage.removeItem("hostland_pending_sub");
              break;
            }
          } catch {}
          await new Promise((r) => setTimeout(r, 1500));
        }
        if (!confirmed) {
          // Payment went through but activation pending — show success anyway, /auth/me will sync
          localStorage.removeItem("hostland_pending_sub");
        }
      }
      onSuccess();
    } else if (!paymentIntent) {
      // Redirect happened (3D Secure) — PaymentSuccess page will handle confirmation
      // Do nothing, browser is redirecting
    }
    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={onCancel} className="text-zinc-400 hover:text-zinc-600 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="text-sm font-bold text-zinc-900">{pkg.name}</p>
          <p className="text-xs text-zinc-400">{pkg.price}</p>
        </div>
        <div className="w-5" />
      </div>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p className="text-xs text-red-500 font-medium text-center">{error}</p>}
      <button type="submit" disabled={!stripe || processing}
        className="w-full py-3.5 rounded-2xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
        style={{ background: ACCENT }}
        data-testid="pay-btn">
        {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5" /> Paga {pkg.price}</>}
      </button>
    </form>
  );
};

/* ═══ Wrapper that loads Elements with clientSecret ═══ */
const StripePaymentWrapper = ({ plan, token, onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const createSub = async () => {
      try {
        const res = await axios.post(`${API}/payments/create-subscription`, { package_type: plan }, { headers: { Authorization: `Bearer ${token}` } });
        setClientSecret(res.data.client_secret);
        localStorage.setItem("hostland_pending_sub", res.data.subscription_id);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Errore creazione abbonamento");
        onCancel();
      }
      setLoading(false);
    };
    createSub();
  }, [plan, token]);

  if (loading || !clientSecret) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-zinc-300" /></div>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "flat", variables: { colorPrimary: ACCENT, borderRadius: "12px", fontFamily: "-apple-system, system-ui, sans-serif" } } }}>
      <PaymentForm plan={plan} onSuccess={onSuccess} onCancel={onCancel} token={token} />
    </Elements>
  );
};

/* ═══ TRIAL BANNER ═══ */
export const TrialBanner = ({ trialInfo, onSubscribe }) => {
  const [remaining, setRemaining] = useState(trialInfo?.trial_remaining_seconds || 0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => { if (trialInfo?.trial_remaining_seconds > 0) setRemaining(trialInfo.trial_remaining_seconds); }, [trialInfo]);
  useEffect(() => { if (remaining <= 0) return; const t = setInterval(() => setRemaining(p => Math.max(0, p - 1)), 1000); return () => clearInterval(t); }, [remaining]);

  if (dismissed || (!trialInfo?.is_trial && !trialInfo?.trial_expired)) return null;
  const isExpired = trialInfo?.trial_expired || remaining <= 0;

  return (
    <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="sticky top-0 z-[60] w-full" data-testid="trial-banner">
      <div className="px-4 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-white text-sm font-medium" style={{ background: isExpired ? "#dc2626" : ACCENT }}>
        {isExpired ? (
          <>
            <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 shrink-0" /><span className="text-center">Prova gratuita terminata! Abbonati per continuare.</span></div>
            <button onClick={onSubscribe} className="bg-white text-red-600 px-5 py-2 rounded-full text-sm font-bold hover:bg-red-50 transition-colors shrink-0" data-testid="trial-subscribe-btn">Abbonati Ora</button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2"><Clock className="w-5 h-5 shrink-0" /><span>Prova gratuita: <strong>{formatTime(remaining)}</strong> rimanenti</span></div>
            <div className="flex items-center gap-2">
              <button onClick={onSubscribe} className="bg-white/20 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-bold hover:bg-white/30 transition-colors shrink-0" data-testid="trial-upgrade-btn">Abbonati</button>
              <button onClick={() => setDismissed(true)} className="p-1 hover:bg-white/20 rounded-full transition-colors shrink-0"><X className="w-4 h-4" /></button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

/* ═══ TRIAL EXPIRED WALL — Blocks dashboard, inline Stripe Elements ═══ */
export const TrialExpiredWall = ({ trialInfo, onSelectPlan, onLogout, token }) => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const daysLeft = trialInfo?.days_until_deletion ?? 0;

  const handlePaymentSuccess = () => {
    toast.success("Abbonamento attivato! Benvenuto su Hostland.");
    window.location.reload();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Account eliminato.");
      if (onLogout) onLogout();
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella cancellazione");
    }
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-[999] bg-red-600 flex items-center justify-center p-4 overflow-y-auto" data-testid="trial-expired-wall">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl">
        {!selectedPlan ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><AlertTriangle className="w-8 h-8 text-red-500" /></div>
              <h1 className="text-2xl font-black text-zinc-900 mb-2">Il tuo periodo di prova è terminato</h1>
              <p className="text-sm text-zinc-500 leading-relaxed">
                I tuoi contenuti sono in <strong className="text-red-600">pausa</strong>. I tuoi ospiti non possono piu accedervi.
                {daysLeft > 0 && <span className="block mt-2 text-red-500 font-bold">I dati verranno cancellati tra {Math.ceil(daysLeft)} giorni.</span>}
              </p>
            </div>
            <div className="space-y-3 mb-6">
              <button onClick={() => setSelectedPlan("monthly")} className="w-full p-4 rounded-2xl border-2 border-zinc-200 hover:border-zinc-400 transition-colors text-left" data-testid="wall-plan-monthly">
                <div className="flex items-center justify-between">
                  <div><p className="font-bold text-zinc-900">Mensile</p><p className="text-xs text-zinc-400 mt-0.5">2 strutture incluse</p></div>
                  <div className="text-right"><p className="text-xl font-bold text-zinc-900">€9,90</p><p className="text-[10px] text-zinc-400">/mese</p></div>
                </div>
              </button>
              <button onClick={() => setSelectedPlan("yearly")} className="w-full p-4 rounded-2xl border-2 transition-colors text-left relative overflow-hidden" style={{ borderColor: ACCENT }} data-testid="wall-plan-yearly">
                <div className="absolute top-0 right-0 text-white text-[9px] font-bold px-2 py-0.5 rounded-bl-lg" style={{ background: ACCENT }}>RISPARMI 50%</div>
                <div className="flex items-center justify-between">
                  <div><p className="font-bold text-zinc-900">Annuale</p><p className="text-xs text-zinc-400 mt-0.5">Strutture illimitate</p></div>
                  <div className="text-right"><p className="text-xl font-bold text-zinc-900">€59,90</p><p className="text-[10px] text-zinc-400">/anno</p></div>
                </div>
              </button>
            </div>
            <div className="border-t border-zinc-100 pt-4">
              {!showConfirmDelete ? (
                <button onClick={() => setShowConfirmDelete(true)} className="text-xs text-zinc-400 hover:text-red-500 transition-colors flex items-center gap-1.5 mx-auto" data-testid="wall-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Non mi interessa, cancella il mio account e tutti i dati
                </button>
              ) : (
                <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                  <p className="text-xs text-red-600 font-bold text-center">Sei sicuro? Tutti i dati verranno eliminati per sempre.</p>
                  <div className="flex gap-2 justify-center">
                    <button onClick={() => setShowConfirmDelete(false)} className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-xl text-xs font-bold">Annulla</button>
                    <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bold disabled:opacity-50 flex items-center gap-2" data-testid="wall-confirm-delete-btn">
                      {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Elimina tutto
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </>
        ) : (
          /* ═══ Stripe Elements Payment Form ═══ */
          <StripePaymentWrapper plan={selectedPlan} token={token} onSuccess={handlePaymentSuccess} onCancel={() => setSelectedPlan(null)} />
        )}
      </motion.div>
    </div>
  );
};

/* ═══ SUBSCRIPTION POPUP (for active trial users) ═══ */
export const SubscriptionPopup = ({ isOpen, onClose, onSelectPlan, token }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  if (!isOpen) return null;

  const handlePaymentSuccess = () => {
    toast.success("Abbonamento attivato!");
    window.location.reload();
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4 overflow-y-auto" data-testid="subscription-popup">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
          {!selectedPlan ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: `${ACCENT}10` }}><Sparkles className="w-7 h-7" style={{ color: ACCENT }} /></div>
                <h2 className="text-xl font-bold text-zinc-900 mb-1">Attiva il tuo abbonamento</h2>
                <p className="text-sm text-zinc-500">Scegli un piano per continuare a usare Hostland.</p>
              </div>
              <div className="space-y-3 mb-4">
                <button onClick={() => setSelectedPlan("monthly")} className="w-full p-4 rounded-2xl border-2 border-zinc-200 hover:border-zinc-400 transition-colors text-left" data-testid="popup-plan-monthly">
                  <div className="flex items-center justify-between">
                    <div><p className="font-bold text-zinc-900">Mensile</p><p className="text-xs text-zinc-400 mt-0.5">2 case incluse</p></div>
                    <div className="text-right"><p className="text-xl font-bold text-zinc-900">€9,90</p><p className="text-[10px] text-zinc-400">/mese</p></div>
                  </div>
                </button>
                <button onClick={() => setSelectedPlan("yearly")} className="w-full p-4 rounded-2xl border-2 transition-colors text-left relative overflow-hidden" style={{ borderColor: ACCENT }} data-testid="popup-plan-yearly">
                  <div className="absolute top-0 right-0 text-white text-[9px] font-bold px-2 py-0.5 rounded-bl-lg" style={{ background: ACCENT }}>RISPARMI 50%</div>
                  <div className="flex items-center justify-between">
                    <div><p className="font-bold text-zinc-900">Annuale</p><p className="text-xs text-zinc-400 mt-0.5">Strutture illimitate</p></div>
                    <div className="text-right"><p className="text-xl font-bold text-zinc-900">€59,90</p><p className="text-[10px] text-zinc-400">/anno</p></div>
                  </div>
                </button>
              </div>
              <button onClick={onClose} className="w-full text-center text-xs text-zinc-400 hover:text-zinc-600 transition-colors py-2" data-testid="popup-dismiss">Ci penso dopo</button>
            </>
          ) : (
            <StripePaymentWrapper plan={selectedPlan} token={token} onSuccess={handlePaymentSuccess} onCancel={() => setSelectedPlan(null)} />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
